
import { motion } from 'framer-motion';

interface AdvertisePlansProps {
  onSelectPlan: (plan: string) => void;
}

const plans = [
  {
    name: 'スポットプラン',
    duration: '1週間〜',
    price: '$5,000',
    priceNote: '〜/週',
    description: 'イベントやキャンペーンに最適な短期プラン',
    features: [
      '1週間から掲載可能',
      '1日8時間の掲載枠',
      'クリエイティブ制作サポート',
      '掲載レポート提供',
    ],
    color: 'from-gray-600 to-gray-800',
    popular: false,
  },
  {
    name: 'スタンダードプラン',
    duration: '1ヶ月〜',
    price: '$15,000',
    priceNote: '〜/月',
    description: '継続的なブランド露出に最適なプラン',
    features: [
      '1ヶ月から掲載可能',
      '1日12時間の掲載枠',
      'クリエイティブ制作込み',
      '週次レポート提供',
      '掲載位置の優先選択',
    ],
    color: 'from-electric-blue to-cyan-400',
    popular: true,
  },
  {
    name: 'プレミアムプラン',
    duration: '3ヶ月〜',
    price: '$35,000',
    priceNote: '〜/月',
    description: '最大露出を実現するエンタープライズプラン',
    features: [
      '3ヶ月から掲載可能',
      '24時間フル掲載',
      'プレミアムロケーション確約',
      '専任アカウントマネージャー',
      'リアルタイムダッシュボード',
      '競合排他オプション',
    ],
    color: 'from-neon-gold to-amber-500',
    popular: false,
  },
];

export default function AdvertisePlans({ onSelectPlan }: AdvertisePlansProps) {
  const handleSelectPlan = (planName: string) => {
    onSelectPlan(planName);
    const formElement = document.getElementById('inquiry-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="plans" 
      className="py-12 px-6 relative"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/b421b1a906d45857dec96967ee2c320a.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm"></div>
      <div className="max-w-[1400px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-electric-blue font-medium text-xs uppercase tracking-wider">
            Pricing Plans
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-2">
            デジタルビルボード広告 料金プラン
          </h2>
          <p className="text-gray-600 text-sm max-w-2xl mx-auto">
            目的とご予算に合わせて最適な屋外広告プランをお選びください
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl overflow-hidden bg-white ${
                plan.popular ? 'ring-2 ring-electric-blue shadow-xl scale-105' : 'shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-electric-blue to-cyan-400 text-white text-center py-1.5 text-xs font-medium">
                  最も人気
                </div>
              )}
              <div className={`p-5 ${plan.popular ? 'pt-10' : ''}`}>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                  <i className="ri-advertisement-line text-lg text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {plan.name}
                </h3>
                <p className="text-gray-500 text-xs mb-3">{plan.duration}</p>
                <div className="mb-4">
                  <span className="text-2xl font-black text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 text-xs">{plan.priceNote}</span>
                </div>
                <p className="text-gray-600 text-xs mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-check-line text-electric-blue text-sm mt-0.5"></i>
                      <span className="text-gray-700 text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan.name)}
                  className={`block w-full py-2.5 rounded-full font-medium text-sm text-center transition-all cursor-pointer whitespace-nowrap ${
                    plan.popular
                      ? 'bg-gradient-to-r from-electric-blue to-cyan-400 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:scale-105'
                  }`}
                >
                  このプランで申し込む
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-gray-500 text-xs mt-8"
        >
          ※ 料金はロケーション・時期により変動します。詳細はお問い合わせください。
        </motion.p>
      </div>
    </section>
  );
}
